import { getUserMediaConstraints, isIOSDevice } from './micPermission';
import { pickRecorderMimeType } from './recorderMimeType';

const IOS_CALIBRATE_MS = 450;
const IOS_SILENCE_AFTER_SPEECH_MS = 1200;
const IOS_MIN_SPEECH_MS = 300;
const IOS_SPEECH_MULTIPLIER = 2.2;
const IOS_MIN_SPEECH_BYTES = 450;

/**
 * Vanilla MediaRecorder wrapper — no ScriptProcessorNode / WASM encoders.
 * Caller must invoke start() inside a user gesture on mobile.
 */
export class AudioRecorder {
  constructor() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.sliceSizes = [];
    this.calibrationSizes = [];
    this.heardSpeech = false;
    this.firstSpeechAt = 0;
    this.lastSpeechAt = 0;
    this.noiseFloorBytes = 0;
    this.calibrationDone = false;
    this.recordingStartedAt = 0;
    this.mimeType = '';
    this.ownsStream = false;
  }

  get isRecording() {
    return this.recorder?.state === 'recording';
  }

  get mediaStream() {
    return this.stream;
  }

  speechThresholdBytes() {
    if (!this.calibrationDone || this.noiseFloorBytes <= 0) {
      return IOS_MIN_SPEECH_BYTES;
    }
    return Math.max(
      this.noiseFloorBytes * IOS_SPEECH_MULTIPLIER,
      this.noiseFloorBytes + 280,
      IOS_MIN_SPEECH_BYTES,
    );
  }

  noteSliceSize(size) {
    const now = Date.now();
    if (!this.calibrationDone) {
      if (size > 0) {
        this.calibrationSizes.push(size);
      }
      if (
        now - this.recordingStartedAt >= IOS_CALIBRATE_MS &&
        this.calibrationSizes.length >= 2
      ) {
        const sum = this.calibrationSizes.reduce((acc, value) => acc + value, 0);
        this.noiseFloorBytes = sum / this.calibrationSizes.length;
        this.calibrationDone = true;
      }
      return;
    }

    if (size >= this.speechThresholdBytes()) {
      if (!this.firstSpeechAt) {
        this.firstSpeechAt = now;
      }
      this.lastSpeechAt = now;
      this.heardSpeech = true;
    }
  }

  async start(existingStream = null) {
    if (this.isRecording) return;

    this.ownsStream = !existingStream;
    const stream =
      existingStream ||
      (await navigator.mediaDevices.getUserMedia(getUserMediaConstraints()));
    this.stream = stream;
    this.mimeType = pickRecorderMimeType();

    const options = this.mimeType ? { mimeType: this.mimeType } : undefined;
    try {
      this.recorder = options
        ? new MediaRecorder(stream, options)
        : new MediaRecorder(stream);
    } catch {
      this.recorder = new MediaRecorder(stream);
    }
    if (!this.mimeType) {
      this.mimeType = this.recorder.mimeType || '';
    }

    this.chunks = [];
    this.sliceSizes = [];
    this.calibrationSizes = [];
    this.heardSpeech = false;
    this.firstSpeechAt = 0;
    this.lastSpeechAt = 0;
    this.noiseFloorBytes = 0;
    this.calibrationDone = false;
    this.recordingStartedAt = Date.now();
    this.recorder.ondataavailable = (event) => {
      const size = event.data?.size || 0;
      this.sliceSizes.push(size);
      if (this.sliceSizes.length > 32) {
        this.sliceSizes.shift();
      }
      if (size > 0) {
        this.chunks.push(event.data);
      }
      if (isIOSDevice()) {
        this.noteSliceSize(size);
      }
    };
    // Timeslice required on Safari — without it, ondataavailable may never fire.
    this.recorder.start(250);
  }

  /** iOS fallback: end-of-utterance from adaptive MediaRecorder slice sizes. */
  detectUtteranceEndFromChunks() {
    if (!isIOSDevice() || !this.heardSpeech || !this.lastSpeechAt || !this.calibrationDone) {
      return false;
    }
    const now = Date.now();
    if (now - this.firstSpeechAt < IOS_MIN_SPEECH_MS) {
      return false;
    }
    return now - this.lastSpeechAt >= IOS_SILENCE_AFTER_SPEECH_MS;
  }

  stop() {
    return new Promise((resolve, reject) => {
      const rec = this.recorder;
      if (!rec || rec.state === 'inactive') {
        this.release();
        resolve(null);
        return;
      }

      let settled = false;
      const finishStop = () => {
        if (settled) return;
        settled = true;
        rec.onstop = () => {
          const type = this.mimeType || rec.mimeType || 'audio/webm';
          const blob = this.chunks.length ? new Blob(this.chunks, { type }) : null;
          this.release();
          resolve(blob);
        };
        rec.onerror = () => {
          this.release();
          reject(new Error('recording-failed'));
        };
        try {
          rec.stop();
        } catch (err) {
          this.release();
          reject(err);
        }
      };

      const appendChunk = (event) => {
        const size = event.data?.size || 0;
        if (size > 0) {
          this.chunks.push(event.data);
        }
      };

      try {
        if (typeof rec.requestData === 'function' && rec.state === 'recording') {
          const onFlush = (event) => {
            rec.removeEventListener('dataavailable', onFlush);
            appendChunk(event);
            finishStop();
          };
          rec.addEventListener('dataavailable', onFlush);
          rec.requestData();
          // Safari sometimes omits the flush event — stop after a short wait.
          window.setTimeout(() => {
            rec.removeEventListener('dataavailable', onFlush);
            finishStop();
          }, 180);
          return;
        }
        finishStop();
      } catch (err) {
        this.release();
        reject(err);
      }
    });
  }

  abort() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.release();
  }

  release() {
    this.recorder = null;
    this.chunks = [];
    this.sliceSizes = [];
    this.calibrationSizes = [];
    this.heardSpeech = false;
    this.firstSpeechAt = 0;
    this.lastSpeechAt = 0;
    this.noiseFloorBytes = 0;
    this.calibrationDone = false;
    this.recordingStartedAt = 0;
    if (this.stream && this.ownsStream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
    }
    this.stream = null;
    this.ownsStream = false;
  }
}
